import { jsx as _jsx } from "react/jsx-runtime";
import { safeProp } from '@lc/libutils';
/**
 * Event handler to stop the propagation of events
 */
const stop = (e) => {
    try {
        e.stopPropagation();
    }
    catch {
        //
    }
};
/**
 * List of all supported event types
 */
const names = [
    // Clipboard Events
    'onCopy',
    'onCut',
    'onPaste',
    // Composition Events
    'onCompositionEnd',
    'onCompositionStart',
    'onCompositionUpdate',
    // Focus Events
    'onFocus',
    'onBlur',
    // Form Events
    'onChange',
    'onBeforeInput',
    'onInput',
    'onReset',
    'onSubmit',
    'onInvalid',
    // Image Events
    'onLoad',
    'onError',
    // Keyboard Events
    'onKeyDown',
    'onKeyPress',
    'onKeyUp',
    // Media Events
    'onAbort',
    'onCanPlay',
    'onCanPlayThrough',
    'onDurationChange',
    'onEmptied',
    'onEncrypted',
    'onEnded',
    'onLoadedData',
    'onLoadedMetadata',
    'onLoadStart',
    'onPause',
    'onPlay',
    'onPlaying',
    'onProgress',
    'onRateChange',
    'onSeeked',
    'onSeeking',
    'onStalled',
    'onSuspend',
    'onTimeUpdate',
    'onVolumeChange',
    'onWaiting',
    // MouseEvents
    'onAuxClick',
    'onClick',
    'onContextMenu',
    'onDoubleClick',
    'onDrag',
    'onDragEnd',
    'onDragEnter',
    'onDragExit',
    'onDragLeave',
    'onDragOver',
    'onDragStart',
    'onDrop',
    'onMouseDown',
    'onMouseEnter',
    'onMouseLeave',
    'onMouseMove',
    'onMouseOut',
    'onMouseOver',
    'onMouseUp',
    // Selection Events
    'onSelect',
    // Touch Events
    'onTouchCancel',
    'onTouchEnd',
    'onTouchMove',
    'onTouchStart',
    // Pointer Events
    'onPointerDown',
    'onPointerMove',
    'onPointerUp',
    'onPointerCancel',
    //'onPointerEnter',
    //'onPointerLeave',
    'onPointerOver',
    'onPointerOut',
    //'onGotPointer', // not in the version of React we use
    //'onLostPointer', // not in the version of React we use
    // UI Events
    'onScroll',
    // Wheel Events
    'onWheel',
    // Animation Events
    'onAnimationStart',
    'onAnimationEnd',
    'onAnimationIteration',
    // Transition Events
    'onTransitionEnd',
];
/**
 * Set of props to pass to the wrapper to stop propagation
 */
const handlers = Object.fromEntries(names.map(name => [name, stop]));
/**
 * Renders a span that works transparently, with `display: contents`
 */
function TransparentSpan({ children, ...props }) {
    return (
    // @ts-expect-error react event type vs native event type...
    _jsx("span", { style: { display: 'contents' }, ...props, children: children }));
}
/**
 * React component that catches and stops the propagation of all supported events
 * Needs to render an element (defaults to a [[TransparentSpan]], but can be customized with the `Elem` prop)
 */
export function StopPropagation({ children, Elem = TransparentSpan, allowed, disabled = false, }) {
    const props = disabled
        ? {}
        : { ...handlers };
    for (const key of allowed ?? [])
        delete props[safeProp(key)];
    return _jsx(Elem, { ...props, children: children });
}
//# sourceMappingURL=data:application/json;base64,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